<script setup>
import { uniq } from 'lodash-es';
import { useBiDataFormatter } from '~/bi/components/widgets/table-widget/composables/bi-table-data-formatter.composable.js';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { useCsvParser } from '~/common/composables/csv-parser.composable.js';
import { sleep } from '~/common/utils/common.utils';

const props = defineProps({
  columns: {
    type: Array,
    default: () => [],
  },
  data: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['close', 'import']);

const { $toast } = useCommonImports();
const { parseFile } = useCsvParser();
const { buildTableColumns } = useBiDataFormatter();

const form = ref({ file: null });
const state = reactive({
  is_submitting: false,

  new_columns: [],
  new_data: [],

  added_columns: [],
  deleted_columns: [],
});

async function createData() {
  try {
    const file = form.value.file;
    if (!file) {
      state.new_columns = [];
      state.new_data = [];
      state.added_columns = [];
      state.deleted_columns = [];
      return;
    }
    let parsed_data = await parseFile(file);
    parsed_data = parsed_data.map(row => row.map(cell => String(cell ?? '').replace(/[\n\t]/g, '')));
    state.new_columns = parsed_data.splice(0, 1)[0].filter(column => column.trim() !== '');
    validateColumns(state.new_columns);
    state.new_data = parsed_data.map((row) => {
      return row.reduce((obj, value, i) => {
        obj[state.new_columns[i]] = value;
        return obj;
      }, {});
    });

    const old_columns = props.columns.map(column => column.title);

    state.deleted_columns = old_columns.filter(column => !state.new_columns.includes(column));
    state.added_columns = state.new_columns.filter(column => !old_columns.includes(column));
  }
  catch (error) {
    logger.log(`Error:`, error);
    $toast({
      title: 'Error',
      text: error.message,
      type: 'error',
    });
  }
}

function validateColumns(columns) {
  const seen = new Set();

  for (const column of columns) {
    if (seen.has(column)) {
      throw new Error(`Duplicates found for the column: ${column}`);
    }
    if (column.length > 48) {
      throw new Error(
        `Invalid column name "${column}". Column name must not exceed 48 characters.`,
      );
    }
    seen.add(column.title);
  }
}

async function importData() {
  try {
    state.is_submitting = true;
    await sleep(1000);

    const table_columns = await new Promise((resolve, reject) => {
      try {
        const result = buildTableColumns(state.new_data, state.new_columns);
        resolve(result);
      }
      catch (error) {
        reject(error);
      }
    });
    const columns_map = props.columns.reduce((acc, column) => {
      acc[column.title] = column;
      return acc;
    }, {});

    const final_columns = table_columns.map((column, index) => {
      column.order_index = index;
      const old_column = columns_map[column.title];
      if (old_column) {
        if (column.allowed_types.includes(old_column.type)) {
          column.type = old_column.type;
          if (old_column.type === 'dropdown') {
            const unique_values = uniq(state.new_data.map(row => row[column.title])).filter(val => !!val?.length);
            column.options = unique_values;
            column.allow_multi_select = old_column.allow_multi_select;
          }
        }
      }
      return column;
    });
    emit('import', {
      columns: final_columns,
      data: state.new_data,
    });
  }
  catch (error) {
    logger.log(`Error:`, error);
    $toast({
      title: 'Error',
      text: error.message,
      type: 'error',
    });
  }
  finally {
    await sleep(1000);
    state.is_submitting = false;
  }
}
</script>

<template>
  <HawkModalContainer content_class="!w-[700px]">
    <Vueform
      v-model="form"
      sync size="sm"
      :display-errors="false"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, label: 4, wrapper: 12 },
      }"
      :loading="state.is_submitting"
      :endpoint="importData"
    >
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #title>
            {{ $t('Import dataset') }}
          </template>
        </HawkModalHeader>
        <HawkModalContent>
          <FileElement
            name="file"
            class="mb-5"
            :label="$t('Choose file')"
            :presets="['hawk_file_element']"
            :options="{
              clickable_text: $t('Click to upload'),
              text: 'or drag and drop',
              description: '.csv, .xlsx',
            }"
            rules="required"
            accept=".xlsx,.csv"
            :auto="false"
            :drop="true"
            :use_uppy="false"
            @change="createData"
          />

          <div v-if="state.added_columns.length" class="grid grid-cols-12 mb-3">
            <div class="col-span-4 text-sm text-gray-900 font-medium">
              {{ $t('Added Columns') }}
            </div>
            <div class="col-span-8">
              <div class="flex items-center flex-wrap">
                <HawkBadge v-for="col in state.added_columns" :key="col" color="green" class="mr-2 mb-2">
                  {{ col }}
                </HawkBadge>
              </div>
            </div>
          </div>
          <div v-if="state.deleted_columns.length" class="grid grid-cols-12 mb-3">
            <div class="col-span-4 text-sm text-gray-900 font-medium">
              {{ $t('Deleted Columns') }}
            </div>
            <div class="col-span-8">
              <div class="flex items-center flex-wrap">
                <HawkBadge v-for="col in state.deleted_columns" :key="col" color="red" class="mr-2 mb-2">
                  {{ col }}
                </HawkBadge>
              </div>
            </div>
          </div>
        </HawkModalContent>
        <HawkModalFooter>
          <template #right>
            <div class="flex justify-end">
              <HawkButton color="primary" type="outlined" class="mr-3" @click="emit('close')">
                {{ $t('Cancel') }}
              </HawkButton>
              <ButtonElement
                submits name="submit" :button-label="$t('Import')"
                :disabled="!state.new_data.length"
              />
            </div>
          </template>
        </HawkModalFooter>
      </div>
    </Vueform>
  </HawkModalContainer>
</template>
